import React, { useEffect, useState } from 'react';
// import { AutomationScope } from '../AutomationDashboard';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
// import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { ExternalLink, Image, Lock, X } from 'lucide-react';
import { Button } from '../ui/button';

import * as gtUtil from "../../../../../../core/utility";
import { PostMediaProps, AutomationContentType, AutomationScope, ZapTopTabsType, ZapDasboardTabsType } from 'custom-type';
import { InstagramDMAutomation } from "../../../../../../core/services/dm-automation-instagram.service";
import ContentLoading from '../../../../../../components/loader/loading';
import { Link, NavLink } from 'react-router-dom';
import useBodyClass from '../../../../../../hooks/useBodyClass';
// import { AutomationContentType } from '../SetupWizard';
// import { content } from 'html2canvas/dist/types/css/property-descriptors/content';

const instagramDMAutomationService: InstagramDMAutomation = new InstagramDMAutomation();

// Instagram returns media_product_type (FEED / REELS / STORY / AD) and media_type
// (IMAGE / VIDEO / CAROUSEL_ALBUM). Collapse the pair into one short label so the
// row can say what kind of content it is at a glance.
const getMediaTypeLabel = (post: PostMediaProps): string => {
  const product = (post?.media_product_type || '').toUpperCase();
  const type = (post?.media_type || '').toUpperCase();
  if (product === 'REELS') return 'Reel';
  if (product === 'STORY') return 'Story';
  if (product === 'AD') return 'Ad';
  if (type === 'CAROUSEL_ALBUM') return 'Carousel';
  if (type === 'VIDEO') return 'Video';
  if (type === 'IMAGE') return 'Photo';
  return 'Post';
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "18 Sep 2026" for posts. Stories only live 24h, so they also carry the time.
// Returns '' for a missing/unparseable timestamp — the chip is then not rendered
// (posts restored from a saved automation may not carry one).
// Formatted by hand rather than with toLocaleDateString(locale, options) so web and
// the app read identically regardless of device locale / Hermes' Intl build.
const formatPostedAt = (timestamp?: string, withTime: boolean = false): string => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  const date = `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  if (!withTime) return date;
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const minutes = `${d.getMinutes()}`.padStart(2, '0');
  return `${date}, ${hours12}:${minutes} ${hours24 < 12 ? 'AM' : 'PM'}`;
};

interface StepSelectPostsProps {
  scope: AutomationScope;
  setScope: (scope: AutomationScope) => void;
  entitlements: any;
  /** Leaves the wizard and lands on the subscription screen (not a popup). */
  handleGoToUpgrade: () => void;
  contentType: AutomationContentType;
  selectedPosts: PostMediaProps[];
  instagramAccount: any;
  handlePostClick: (media: PostMediaProps, scope: AutomationScope) => void;
  setTopTab: (type: ZapTopTabsType) => void;
  onClose: () => void;
  setActiveTab: (type: ZapDasboardTabsType) => void;
  totalActiveAutomationPosts: number;
  setTotalActiveAutomationPosts: React.Dispatch<
    React.SetStateAction<number>
  >;
  maxActiveAutomationPosts: number;
}

export function StepSelectPosts({
  scope,
  setScope,
  entitlements,
  handleGoToUpgrade,
  contentType,
  selectedPosts,
  instagramAccount,
  handlePostClick,
  setTopTab,
  onClose,
  setActiveTab,
  totalActiveAutomationPosts,
  setTotalActiveAutomationPosts,
  maxActiveAutomationPosts
}: StepSelectPostsProps) {

  // New Logic for single vs multiple selection
  // Step 2 to set posts
  const [loading, setLoading] = useState(false);
  const [igMediaData, setIgMediaData] = useState<PostMediaProps[]>([]);
  // One page of media. See more reveals another page of the same size, so the
  // list grows in even rows rather than 3 then 3 then 3.
  const MEDIA_PAGE_SIZE = 4;
  const [visibleCount, setVisibleCount] = useState(MEDIA_PAGE_SIZE);

  const isStory = contentType === 'story';

  // "Any Post" / "Any Story" is a paid-plan feature (any_media_automation_enabled).
  // The card stays visible but routes to the upgrade popup, so free users can see
  // what they would get rather than the option silently not existing.
  const canUseAnyMedia = gtUtil.isAnyMediaAutomationAllowed(entitlements);

  // Once something is picked, the step heading and the scope cards give way to a
  // sticky strip of micro thumbnails, so the selection stays in view while the list
  // scrolls. Deselecting everything brings them back — that is also the way back to
  // "Any Post".
  const hasSelection = scope !== 'all' && selectedPosts.length > 0;

  // Clearing goes through handlePostClick per item rather than replacing the array,
  // so each removal also gives back its slot in totalActiveAutomationPosts (the
  // plan-limit counter the parent keeps).
  const handleClearAll = () => {
    [...selectedPosts].forEach((post) => handlePostClick(post, 'multiple'));
  };

  // const handleSingleSelect = (shortcode: string) => {
  //   const post = igMediaData.find((post) => post.shortcode === shortcode);
  //   if (post) {
  //     handlePostClick(post, 'single');
  //   }
  // };

  const handleMultipleToggle = (shortcode: string) => {
    const post = igMediaData.find((post) => post.shortcode === shortcode);
    if (post) {
      handlePostClick(post, 'multiple');
    }
  };

  // Get instagram posts from influencer account
  async function getInstagramPostMedias() {
    try {
      setLoading(true);
      const res = await instagramDMAutomationService.getInstagramPostMedias({
        ig_access_token: instagramAccount[0]?.ig_access_token,
        connected_instagram_account_id: instagramAccount[0]?.connected_instagram_account_id
      });

      if (res.status === gtUtil.Constants.SUCCESS) {
        setIgMediaData(res?.data?.media?.data || []);
      }
    } catch (error) {
      console.error("Error fetching getInstagramPostMedias:", error);
    } finally {
      setLoading(false);
    }
  }

  // Get instagram stories from influencer account
  async function getInstagramStoryMedias() {
    try {
      setLoading(true);
      const res = await instagramDMAutomationService.getInstagramStoryMedias({
        ig_access_token: instagramAccount[0]?.ig_access_token,
        connected_instagram_account_id: instagramAccount[0]?.connected_instagram_account_id
      });

      if (res.status === gtUtil.Constants.SUCCESS) {
        setIgMediaData(res?.data?.stories?.data || []);
      }
    } catch (error) {
      console.error("Error fetching getInstagramStoryMedias:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (instagramAccount[0]?.ig_access_token && instagramAccount[0]?.connected_instagram_account_id && contentType === 'post') {
      getInstagramPostMedias();
    } else if (instagramAccount[0]?.ig_access_token && instagramAccount[0]?.connected_instagram_account_id && contentType === 'story') {
      getInstagramStoryMedias();
    }
  }, [instagramAccount]);

  const handleSeeMore = () => {
    setVisibleCount((prev) => prev + MEDIA_PAGE_SIZE);
  };

  const selectedShortcodes = new Set(
    selectedPosts.map((sp) => sp.shortcode)
  );

  const orderedMediaData: PostMediaProps[] = [
    // 1️⃣ Selected posts (in the order they were selected)
    ...selectedPosts,

    // 2️⃣ Remaining IG media (excluding selected)
    ...igMediaData.filter(
      (post) => !selectedShortcodes.has(post.shortcode)
    ),
  ];

  // const orderedMediaData = [
  //   ...selectedPosts.map(p => ({ ...p, __selected: true })),
  //   ...igMediaData
  //     .filter(p => !selectedShortcodes.has(p.shortcode))
  //     .map(p => ({ ...p, __selected: false })),
  // ];
  
  // if (scope === 'single') {
  //   return (
  //     <div className="space-y-4">
  //       <div>
  //         <h3 className="mb-2">Select a {isStory ? 'Story' : 'Post'}</h3>
  //         <p className="text-sm text-gray-500">
  //           Choose the {contentType} to enable {!isStory && 'comment replies and '}DM automation.
  //         </p>
  //       </div>
  //       {
  //         (!loading && igMediaData.length === 0) && 
  //         <span className='mt-[20px] d-block  color-[#2b2b2b] text-[12px] text-center mx-auto w-100 flex justify-content-center'>
  //           {
  //             contentType === 'post' 
  //             ? 
  //             "No Posts Found." 
  //             : 
  //             isStory ? 
  //             "No Stories Found" 
  //             : 
  //             ""
  //           }
  //         </span>
  //       }
  //       <RadioGroup
  //         value={selectedPosts[0]?.shortcode || ''}
  //         onValueChange={handleSingleSelect}
  //       >
  //         <div className="space-y-3">
  //           {
  //             loading 
  //           ? 
  //             <div className='w-100 d-flex justify-content-center align-items-center'><ContentLoading width="80" height="80" /></div> 
  //           :
  //           orderedMediaData.slice(0, visibleCount).map((post, index) => {
  //             const isSelected = selectedPosts.some(
  //               (sp) => sp.shortcode === post.shortcode
  //             );

  //             return (
  //               <Card
  //                 key={post.shortcode}
  //                 className={`cursor-pointer transition-all hover:border-purple-300 ${
  //                   isSelected
  //                     ? 'border-purple-500 bg-[#eee]'
  //                     : 'border-gray-200'
  //                 }`}
  //                 onClick={() => handleSingleSelect(post.shortcode)}
  //               >
  //                 <CardContent className="p-3">
  //                   <div className="flex items-start gap-3">
  //                     <RadioGroupItem value={post.shortcode} id={post.shortcode} className="mt-1" />
  //                     <ImageWithFallback
  //                       src={post?.thumbnail_url ?? post.media_url}
  //                       alt="Post thumbnail"
  //                       className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
  //                     />
  //                     <div className="flex-1 min-w-0 ml-auto">  
  //                       <Label htmlFor={post.shortcode} className="cursor-pointer items-start">
  //                         <p className="text-sm mb-2 line-clamp-2 me-auto">{post.caption}</p>
  //                         <Button
  //                           variant="link"
  //                           size="sm"
  //                           className="h-auto p-0 text-xs text-[#2b2b2b] mt-1"
  //                           onClick={(e) => {
  //                             e.stopPropagation();
  //                             window.open(post.permalink, '_blank');
  //                           }}
  //                         >
  //                           <ExternalLink className="w-3 h-3 mr-0" />
  //                           View on Instagram
  //                         </Button>
  //                       </Label>
  //                     </div>
  //                   </div>
  //                 </CardContent>
  //               </Card>
  //             );
  //           })}
  //         </div>
  //       </RadioGroup>
  //       {igMediaData.length !== 0 && 
  //       <Link
  //         to="javascript:void(0)"
  //         className={`my-2 mx-auto btn btn-outline-primary d-block w-fit-content ${
  //           visibleCount >= igMediaData.length ? 'disabled pointer-events-none opacity-50' : ''
  //         }`}
  //         onClick={visibleCount < igMediaData.length ? handleSeeMore : undefined}
  //       >
  //         {visibleCount < igMediaData.length ? 'See more' : 'See more'}
  //       </Link>
  //       }
  //     </div>
  //   );
  // }
  useBodyClass("page-setup-wizard")
  return (
    <div className="space-y-4">
      {hasSelection && (
        /* Pinned selection summary — sticks to the top of the wizard's scroll
           container so the chosen media stays visible while the list scrolls.
           Clicking a thumbnail removes that item. */
        <div className="sticky top-0 z-20 bg-white pt-2 pb-3 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600 mb-0">
              {selectedPosts.length} {(selectedPosts.length !== 1) ? `${isStory ? 'stories' : 'posts'}` : `${contentType}`} selected
            </p>
            <button
              type="button"
              onClick={handleClearAll}
              className="border-0 bg-transparent p-0 text-xs font-semibold text-[#7c3aed] hover:underline"
            >
              Clear all
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {selectedPosts.map((post) => (
              <div
                key={post.shortcode || post.media_id || post.id}
                onClick={() => handlePostClick(post, 'multiple')}
                title={`Remove this ${isStory ? 'story' : 'post'} from the selection`}
                role="button"
                aria-label={`Remove this ${isStory ? 'story' : 'post'} from the selection`}
                className="relative flex-shrink-0 cursor-pointer"
              >
                <ImageWithFallback
                  src={post.gcp_media_file_url || post.thumbnail_url || post.media_url}
                  alt={`Selected ${isStory ? 'story' : 'post'} thumbnail`}
                  className="w-11 h-11 object-cover rounded-lg"
                />
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-white bg-black text-white">
                  <X className="h-[10px] w-[10px]" />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!hasSelection && (
      <>
      <div>
        <h3 className="mb-2">Select {isStory ? 'Stories' : 'Posts'}</h3>
        <p className="text-sm text-gray-500">
          Choose {isStory ? 'stories' : 'posts'} to enable {!isStory && 'comment replies and '}DM automation.
        </p>
      </div>

      {/* Scope choice: specific media vs catch-all.
          'all' skips media selection entirely and fires on every future
          post/story, so the picker below is hidden while it is active. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card
          className={`cursor-pointer transition-all ${scope !== 'all' ? 'border-purple-500 bg-[#000]' : 'border-gray-200'}`}
          onClick={() => setScope('multiple')}
        >
          <CardContent className="p-3 md:p-4">
            <p className={scope !== 'all' ? 'text-white font-semibold' : 'font-semibold'}>
              Select {isStory ? 'Stories' : 'Posts'}
            </p>
            <p className={`text-[13px] ${scope !== 'all' ? 'text-white' : ''}`}>
              Pick the {isStory ? 'stories' : 'posts'} this Zap should watch.
            </p>
          </CardContent>
        </Card>

        <Card
          className={`transition-all ${scope === 'all' ? 'border-purple-500 bg-[#000]' : 'border-gray-200'} ${canUseAnyMedia ? 'cursor-pointer' : 'cursor-pointer opacity-60'}`}
          onClick={() => {
            if (!canUseAnyMedia) {
              // Straight to the subscription screen, like the Zap hub's locked cards.
              handleGoToUpgrade();
              return;
            }
            setScope('all');
          }}
          aria-disabled={!canUseAnyMedia}
        >
          <CardContent className="p-3 md:p-4">
            {/* OLD (plain orange text, inconsistent with the rest of the app):
                <small className="text-[12px] text-orange-400 ms-2">Upgrade</small> */}
            <div className="d-flex items-center gap-2 flex-wrap">
              <p className={`mb-0 ${scope === 'all' ? 'text-white font-semibold' : 'font-semibold'}`}>
                Any {isStory ? 'Story' : 'Post'}
              </p>
              {!canUseAnyMedia && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f0ff] px-2 py-[2px] text-[11px] font-semibold leading-none text-[#7c3aed]">
                  <Lock className="h-[11px] w-[11px]" />
                  Upgrade
                </span>
              )}
            </div>
            <p className={`text-[13px] ${scope === 'all' ? 'text-white' : ''}`}>
              Runs on every {isStory ? 'story' : 'post'}, including new ones. No selection needed.
            </p>
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {scope === 'all' ? (
        <p className="text-sm text-gray-500">
          This Zap will run on every {isStory ? 'story' : 'post'} you publish. A Zap set up for a
          specific {isStory ? 'story' : 'post'} still takes priority over this one.
        </p>
      ) : (
      <>
      <div className="space-y-3">
        {
          loading 
        ? 
          <div className='w-100 d-flex justify-content-center align-items-center'><ContentLoading width="80" height="80" /></div> 
        :
        // igMediaData.slice(0, visibleCount).map((post, index) => {
        orderedMediaData.slice(0, visibleCount).map((post, index) => {
          const isSelected = selectedPosts.some(
            (sp) => sp.shortcode === post.shortcode
          );

          return (
            <Card
              key={post.shortcode}
              className={`cursor-pointer position-relative z-10 transition-all hover:border-purple-300 ${
                isSelected
                  ? 'border-purple-500 bg-[#eee]'
                  : 'border-gray-200'
              }`}
              onClick={() => handleMultipleToggle(post.shortcode)}
            >
              <CardContent className="p-3 md:p-4 position-relative z-0">
                <div className="flex items-start gap-2 md:gap-4">
                  <Checkbox
                    id={post.shortcode}
                    style={{position:'relative', zIndex:-1}}
                    checked={isSelected}
                    onCheckedChange={() => handleMultipleToggle(post.shortcode)}
                    className="mt-1"
                  />
                  <ImageWithFallback
                    src={post.gcp_media_file_url || post.thumbnail_url || post.media_url}
                    alt="Post thumbnail"
                    className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    {/* Content type + when it was posted, so a thumbnail alone
                        doesn't have to identify the media. */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-[2px] text-[11px] font-semibold leading-none text-gray-600">
                        {getMediaTypeLabel(post)}
                      </span>
                      {formatPostedAt(post.timestamp, isStory) && (
                        <span className="text-[11px] leading-none text-gray-500">
                          {formatPostedAt(post.timestamp, isStory)}
                        </span>
                      )}
                    </div>
                    <div className="d-flex">
                      <Label 
                        htmlFor={post.shortcode} 
                        className="cursor-pointer items-start"
                        style={{position:'relative', zIndex:-1}}
                      >
                        <p className="text-sm mb-2 line-clamp-2 me-auto">{post.caption}</p>
                      </Label>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-[#2b2b2b] mt-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(post.permalink, '_blank');
                        }}
                      >
                        <ExternalLink className="w-3 h-3 " />
                        View on Instagram
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {igMediaData.length !== 0 && 
        <Link
          to="javascript:void(0)"
          className={`my-2 mx-auto btn btn-outline-primary d-block w-fit-content ${
            visibleCount >= igMediaData.length ? 'disabled pointer-events-none opacity-50' : ''
          }`}
          onClick={visibleCount < igMediaData.length ? handleSeeMore : undefined}
        >
          {visibleCount < igMediaData.length ? 'See more' : 'See more'}
        </Link>
      }
      {/* {
        (!loading && igMediaData.length === 0) && 
        (
          (contentType === 'post')
          ?
          <span className='mt-[20px] d-block color-[#2b2b2b] text-[12px] text-center mx-auto w-100 flex justify-content-center'>
            No Posts Found.
          </span>
          :
          <span className='mt-[20px] d-block color-[#2b2b2b] text-[12px] text-center mx-auto w-100 flex justify-content-center'>
            No Stories Found
          </span>
        )
      } */}
      {
        (!loading && igMediaData.length === 0) && 
        (
          (contentType === 'post')
          ?
          <>
            <span className="mt-[20px] d-block color-[#2b2b2b] text-[12px] text-center mx-auto w-100 flex flex-col justify-content-center">
              <span>No Posts Found.</span>
            </span>

            <span className="mt-[20px] d-block color-[#2b2b2b] text-[12px] text-center mx-auto w-100 flex flex-col justify-content-center">
              {/* <NavLink
                to="/influencer/zap/automations/ig-dm-automations?activeTab=settings"
                className="mt-1 text-[11px] text-primary cursor-pointer hover:underline text-center"
              >
                Posts not showing? Try syncing your profile again from Zap Settings.
              </NavLink> */}
              <Button 
                onClick={() => 
                  {
                    onClose();
                    setTopTab('zaps');
                    setActiveTab('settings');
                  }
                }
                className='mt-1 text-[11px] text-primary cursor-pointer hover:underline text-center' 
                variant="outline"
              >
                Posts not showing? Try syncing your profile again from Zap Settings.
              </Button>
            </span>
          </>
          :
          <>
            <span className="mt-[20px] d-block color-[#2b2b2b] text-[12px] text-center mx-auto w-100 flex flex-col justify-content-center">
              <span>No Stories Found.</span>
            </span>

            <span className="mt-[20px] d-block color-[#2b2b2b] text-[12px] text-center mx-auto w-100 flex flex-col justify-content-center">
              {/* <NavLink
                to="/influencer/zap/automations/ig-dm-automations?activeTab=settings"
                className="mt-1 text-[11px] text-primary cursor-pointer hover:underline text-center"
              >
                Stories not showing? Try syncing your profile again from Zap Settings.
              </NavLink> */}
              <Button 
                onClick={() => 
                  {
                    onClose();
                    setTopTab('zaps');
                    setActiveTab('settings');
                  }
                }
                className='mt-1 p-0 md:p-3 border-0 text-[11px] text-primary cursor-pointer hover:underline text-center whitespace-normal' 
                variant="outline"
              >
                Stories not showing? Try syncing your profile again from Zap Settings.
              </Button>
            </span>
          </>
        )
      }

      {(!loading && igMediaData.length !== 0 && !hasSelection) &&
        <p className="text-sm text-gray-500">
          {selectedPosts.length} {(selectedPosts.length !== 1) ? `${contentType === 'story' ? 'stories' : 'posts'}` : `${contentType}`} selected
        </p>
      }
      </>
      )}
    </div>
  );
}